// SPDX-License-Identifier: AGPL-3.0-or-later
// SPDX-FileCopyrightText: 2026 Dobrev IT Ltd
import { useRef, useState, type CSSProperties, type PointerEvent as ReactPointerEvent } from "react";

export interface PipDrag {
  ref: (el: HTMLElement | null) => void;
  style: CSSProperties;
  onPointerDown: (e: ReactPointerEvent) => void;
  onPointerMove: (e: ReactPointerEvent) => void;
  onPointerUp: (e: ReactPointerEvent) => void;
  onPointerCancel: (e: ReactPointerEvent) => void;
}

// usePipDrag makes a fixed-position element draggable around the viewport (clamped so it
// always stays fully on-screen). A press that DOESN'T move past a small threshold counts
// as a tap and calls onTap (e.g. restore the call / swap the main view); a real drag
// suppresses the tap.
export function usePipDrag(onTap: () => void): PipDrag {
  const elRef = useRef<HTMLElement | null>(null);
  const drag = useRef<{ dx: number; dy: number; sx: number; sy: number; moved: boolean } | null>(null);
  const [pos, setPos] = useState<{ x: number; y: number } | null>(null);

  // end finishes a gesture: release the pointer capture, fire a tap if it never moved.
  // Used for BOTH pointerup and pointercancel — the OS fires `pointercancel` (not `up`)
  // when it reclaims the touch at a screen edge, and without handling it the element was
  // left mid-drag (stuck off-screen until the call restarted).
  const end = (e: ReactPointerEvent) => {
    const d = drag.current;
    drag.current = null;
    try {
      elRef.current?.releasePointerCapture(e.pointerId);
    } catch {
      /* wasn't captured */
    }
    if (d && !d.moved) onTap();
  };

  return {
    ref: (el) => {
      elRef.current = el;
    },
    // Don't set `transform` here — the self-view PiP is mirrored via a CSS transform, and
    // overriding it would un-mirror the camera while dragging.
    style: pos ? { left: pos.x, top: pos.y, right: "auto", bottom: "auto" } : {},
    onPointerDown: (e) => {
      const el = elRef.current;
      if (!el) return;
      const r = el.getBoundingClientRect();
      drag.current = { dx: e.clientX - r.left, dy: e.clientY - r.top, sx: e.clientX, sy: e.clientY, moved: false };
      try {
        el.setPointerCapture(e.pointerId);
      } catch {
        /* capture unsupported — drag still works via move events */
      }
    },
    onPointerMove: (e) => {
      const d = drag.current;
      if (!d) return;
      if (Math.hypot(e.clientX - d.sx, e.clientY - d.sy) > 4) d.moved = true;
      const r = elRef.current?.getBoundingClientRect();
      // `|| fallback` (not `??`) so a transient 0 — a <video> can report it mid-layout —
      // doesn't make the clamp's upper bound `innerWidth`, which would fling the PiP off.
      const w = r?.width || 120;
      const h = r?.height || 120;
      setPos({
        x: Math.min(Math.max(0, e.clientX - d.dx), Math.max(0, window.innerWidth - w)),
        y: Math.min(Math.max(0, e.clientY - d.dy), Math.max(0, window.innerHeight - h)),
      });
    },
    onPointerUp: end,
    onPointerCancel: end,
  };
}
